<template>
  <FlotoFixedView>
    <FlotoPageHeader
      :title="`${resource.name}: ${$tc(resource.movementType)}`"
      class="px-4"
      use-divider
    >
      <template v-slot:back-button>
        <slot name="before-title"></slot>
      </template>
      <template v-slot:title-append>
        <FlotoLink
          v-if="!disabled"
          :to="editRoute"
          class="text-neutral md-text-base mx-1"
          as-button
          variant="neutral-light"
          shape="circle"
          :disabled="disabledEdit"
        >
          <MIcon name="pencil" />
        </FlotoLink>
      </template>
      <div class="flex justify-between">
        <small class="text-neutral">
          {{ $t('by') }}
          <FlotoUserDrawer :user-id="resource.createdById" />
          {{ $t('at') }}
          {{ resource.createdTime | datetime }}
          ({{ resource.createdTime | timeago }})
        </small>
      </div>
      <slot name="additional-header-content" />
      <template v-slot:after-title>
        <slot name="actions" />
      </template>
    </FlotoPageHeader>
    <FlotoScrollView>
      <MCol :size="12" class="px-4" :style="defaultStyle">
        <MRow class="flex-no-wrap">
          <MCol
            class="transition-width main-detail-container"
            v-bind="detailSize"
          >
            <slot v-if="resource.remark" name="description">
              <div>
                <small class="text-neutral-light">
                  {{ $tc('remark', 2) }}
                </small>
              </div>
              <div class="description my-2 mr-4">
                <FlotoReadMore :height="20 * 3">
                  <FlotoRichEditor :value="resource.remark" disabled />
                </FlotoReadMore>
              </div>
            </slot>
            <slot name="primary-row">
              <PrimaryRow
                v-model="primaryInfo"
                :disabled="disabled"
                v-bind="$attrs"
                :is-draft="isDraft"
              />
            </slot>
            <slot name="secondary-row">
              <SecondaryRow
                v-model="secondaryInfo"
                :disabled="disabled"
                :resource="resource"
                v-bind="$attrs"
                :is-draft="isDraft"
              />
            </slot>
            <slot name="tags-row">
              <ThirdRow
                v-model="thirdInfo"
                :disabled="disabled"
                :resource="resource"
                :is-approval="isApproval"
                :approval="approval"
                :is-draft="isDraft"
              />
            </slot>
            <slot v-if="allowAttachments" name="attachments">
              <FlotoAttachment
                :disabled="disabled"
                :value="resource.fileAttachments"
                only-list
                @change="updateResource({ fileAttachments: $event })"
              />
            </slot>
            <slot />
          </MCol>
          <MCol
            v-if="hasSidebar"
            v-bind="sidebarSize"
            class="border border-neutral-lighter sidebar-border border-b-0 border-t-0 border-solid transition-width py-6 relative pages-inner-right-slide-panel"
            style="min-height: 120px"
          >
            <slot v-if="sidebarVisible" name="sidebar">
              <div class="overflow-x-hidden">
                <Sidebar
                  v-model="sidebarInfo"
                  :disabled="disabled"
                  :module-name="moduleName"
                  :is-approval="isApproval"
                />
              </div>
            </slot>
            <div class="toggle">
              <a
                href="javascript:;"
                class="text-neutral hover:text-neutral"
                @click="sidebarVisible = !sidebarVisible"
              >
                <MIcon :name="`chevron-${isRtl ? sidebarRtl : sidebarMain}`" />
              </a>
            </div>
          </MCol>
        </MRow>
      </MCol>
    </FlotoScrollView>
  </FlotoFixedView>
</template>
<script>
import { authComputed } from '@state/modules/auth'
import Pick from 'lodash/pick'
import PrimaryRow from './primary-row'
import SecondaryRow from './secondary-row'
import ThirdRow from './third-row'
import Sidebar from './sidebar-info'
export default {
  name: 'Detail',
  components: { PrimaryRow, SecondaryRow, ThirdRow, Sidebar },
  props: {
    resource: { type: Object, required: true },
    updateApi: { type: Function, required: true },
    disabled: { type: Boolean, default: false },
    moduleName: { type: String, default: 'asset_movement' },
    // eslint-disable-next-line
    hasSidebar: { type: Boolean, default: true },
    // eslint-disable-next-line
    allowAttachments: { type: Boolean, default: true },
    sidebarWidth: { type: Object, default: () => ({ size: 3 }) },
    detailWidth: { type: Object, default: () => ({ size: 9 }) },
    defaultSidebarVisible: { type: Boolean, default: false },
    isApproval: { type: Boolean, default: false },
    approval: { type: Object, default: undefined },
    isDraft: { type: Boolean, default: false },
  },
  data() {
    this.defaultStyle = {
      height: `${window.outerHeight}px`,
    }
    return {
      sidebarVisible: this.defaultSidebarVisible,
    }
  },
  computed: {
    ...authComputed,
    editRoute() {
      if (this.resource.movementType === 'send_to_repair') {
        return this.$modules.getModuleRoute(
          'asset-management',
          'movement-sendtorepairedit',
          {
            params: { id: this.resource.id },
          }
        )
      }
      if (this.resource.movementType === 'return_from_repair') {
        return this.$modules.getModuleRoute(
          'asset-management',
          'movement-returnfromrepairedit',
          {
            params: { id: this.resource.id },
          }
        )
      }
      if (this.resource.movementType === 'asset_transfer') {
        return this.$modules.getModuleRoute(
          'asset-management',
          'movement-assettransferedit',
          {
            params: { id: this.resource.id },
          }
        )
      }
      return undefined
    },
    sidebarRtl() {
      return this.sidebarVisible ? 'left' : 'right'
    },
    sidebarMain() {
      return this.sidebarVisible ? 'right' : 'left'
    },
    disabledEdit() {
      return !this.isDraft
    },
    detailSize() {
      if (this.hasSidebar && this.sidebarVisible) {
        return this.detailWidth
      }
      return { size: 12 }
    },
    sidebarSize() {
      if (this.sidebarVisible) {
        return this.sidebarWidth
      }
      return { size: undefined }
    },
    primaryInfo: {
      get() {
        return Pick(this.resource, [
          'statusId',
          'movementDate',
          'fromLocation',
          'toLocation',
          'movementType',
          'fromLocation',
          'toLocation',
        ])
      },
      set(value) {
        this.updateResource(value)
      },
    },
    secondaryInfo: {
      get() {
        return Pick(this.resource, [
          'vendorId',
          'technicianIds',
          'returnableType',
          'expectedDate',
          'movementType',
          'fromDepartment',
          'toDepartment',
        ])
      },
      set(value) {
        this.updateResource(value)
      },
    },
    thirdInfo: {
      get() {
        return Pick(this.resource, ['approvalStatus'])
      },
      set(value) {
        this.updateResource(value)
      },
    },
    attributeTableInfo: {
      get() {
        return Pick(this.resource, ['rules'])
      },
      set(value) {
        this.updateResource(value)
      },
    },
    sidebarInfo: {
      get() {
        return Pick(this.resource, [
          'referenceNumber',
          'closedDate',
          'updatedTime',
          'assetType',
          'approvalAskOnStatusId',
          'movementType',
          'returnableType',
        ])
      },
      set(value) {
        this.updateResource(value)
      },
    },
  },
  methods: {
    updateResource(change) {
      return this.updateApi({ id: this.resource.id, ...change })
    },
  },
}
</script>

<style lang="less" scoped>
.sub-heading {
  @apply flex items-center justify-between;
}

.heading {
  @apply items-start flex-wrap flex flex-col;

  .action {
    line-height: 30px;

    @apply ml-2 flex items-center justify-center absolute right-0 top-0;
  }

  .title {
    @apply overflow-hidden relative;
  }
}

.main-detail-container {
  &.@{ant-prefix}-col-12 {
    width: calc(100% - 34px);
  }
}
</style>
